import { useState } from 'react';
import { AUDIT_ENTITIES, type AuditEntity } from '@stromo/shared';
import { useAuditLog, ENTITY_LABELS, ACTION_LABELS } from '../lib/audit';
import { formatDateTime } from '../lib/format';
import { apiErrorMessage } from '../lib/api';

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [entityType, setEntityType] = useState<AuditEntity | ''>('');
  const [entityId, setEntityId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);

  const audit = useAuditLog({
    entity_type: entityType || undefined,
    entity_id: entityId ? Number(entityId) : undefined,
    from: from || undefined,
    to: to ? `${to}T23:59:59.999Z` : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const total = audit.data?.total ?? 0;
  const items = audit.data?.items ?? [];

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Audit-Log</div>
      </header>

      <div className="content">
        <div className="filter-bar" style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
          <select value={entityType} onChange={(e) => { setEntityType(e.target.value as AuditEntity | ''); setPage(0); }}>
            <option value="">Alle Entitäten</option>
            {AUDIT_ENTITIES.map((e) => (
              <option key={e} value={e}>{ENTITY_LABELS[e]}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Entity-ID"
            value={entityId}
            onChange={(e) => { setEntityId(e.target.value); setPage(0); }}
            style={{ width: 120 }}
          />
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} title="Von" />
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} title="Bis" />
        </div>

        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          {audit.isLoading ? (
            <div className="loading">Lade …</div>
          ) : audit.error ? (
            <div className="empty-state"><p>{apiErrorMessage(audit.error)}</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state"><p>Keine Einträge.</p></div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Benutzer</th>
                  <th>Entität</th>
                  <th>Aktion</th>
                  <th>Feld</th>
                  <th>Alt</th>
                  <th>Neu</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td data-label="Zeitpunkt" style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {formatDateTime(a.created_at)}
                    </td>
                    <td data-label="Benutzer">{a.user_name ?? '—'}</td>
                    <td data-label="Entität">{ENTITY_LABELS[a.entity_type]} #{a.entity_id}</td>
                    <td data-label="Aktion">
                      <span className={`badge ${a.action === 'DELETE' ? 'badge-delayed' : a.action === 'CREATE' ? 'badge-done' : 'badge-progress'}`}>
                        {ACTION_LABELS[a.action]}
                      </span>
                    </td>
                    <td data-label="Feld" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{a.field_name ?? '—'}</td>
                    <td data-label="Alt" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {a.old_value ?? '—'}
                    </td>
                    <td data-label="Neu" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {a.new_value ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 13, color: 'var(--color-text-muted)' }}>
            <span>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} von {total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Zurück
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Weiter →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
